// apps/web/src/components/ImageLightbox.tsx
// 點擊圖片 → 頁內相框放大（不開新分頁）
import { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  open: boolean;
  onClose: () => void;
}

export default function ImageLightbox({ src, alt = '', open, onClose }: ImageLightboxProps) {
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onKey]);

  if (!open || !src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={alt || '圖片預覽'}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default border-0"
        aria-label="關閉預覽"
        onClick={onClose}
      />

      <div className="relative z-10 max-w-4xl w-full animate-scale-in">
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 sm:top-2 sm:right-2 z-20 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="關閉"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="rounded-2xl overflow-hidden bg-white dark:bg-gray-900 shadow-2xl ring-4 ring-white/90 dark:ring-gray-800 p-2 sm:p-3">
          <img
            src={src}
            alt={alt}
            className="w-full max-h-[80vh] object-contain rounded-xl"
            referrerPolicy="no-referrer"
            draggable={false}
          />
        </div>
        {alt ? <p className="mt-3 text-center text-sm text-white/90 drop-shadow">{alt}</p> : null}
      </div>
    </div>
  );
}

/** 可點擊縮圖：觸發 lightbox（外層 class 負責尺寸） */
export function ZoomableImage({
  src,
  alt,
  className = '',
  imgClassName = '',
  onOpen,
}: {
  src: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  onOpen: (src: string, alt?: string) => void;
}) {
  return (
    <button
      type="button"
      className={`group relative block w-full h-full min-h-0 p-0 m-0 border-0 bg-transparent cursor-zoom-in overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alumni-600 focus-visible:ring-offset-2 ${className}`}
      onClick={() => onOpen(src, alt)}
      aria-label={alt ? `放大檢視：${alt}` : '放大檢視圖片'}
    >
      <img
        src={src}
        alt={alt || ''}
        className={`block w-full h-full object-cover ${imgClassName}`}
        referrerPolicy="no-referrer"
        draggable={false}
        loading="eager"
        onError={(e) => {
          // 預設封面失敗時顯示綠底
          const el = e.currentTarget;
          el.style.display = 'none';
          const parent = el.parentElement;
          if (parent && !parent.dataset.fallback) {
            parent.dataset.fallback = '1';
            parent.classList.add('bg-gradient-to-br', 'from-alumni-700', 'to-alumni-500');
          }
        }}
      />
      <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
    </button>
  );
}
